'use client'

import { ListItemText, MenuItem } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { usePathname, useRouter } from 'next/navigation'

import {
  AICLAIMS_WELCOME_PATH,
  isAiClaimsPortalPath,
} from '../../config/aiclaims-routes'
import useAuth from '../../hooks/useAuth'
import { navigateToBrandPortal } from '../../lib/crossDomainAuth'
import { effectiveBrandAccess } from '../../lib/pageAccess'
import { UsersApi } from '../../services'
import { isInternalEmployee } from '../../utils/rbac'

type Props = { onAction?: () => void }

/**
 * Account-menu brand switches: Ai Claims when the user has both brands,
 * Wysehub when already on the Ai Claims portal, and WyseAi for internal staff.
 */
export default function BrandSwitcher({ onAction }: Props) {
  const { user } = useAuth()
  const router = useRouter()
  const pathname = usePathname() || ''
  const queryClient = useQueryClient()
  const onAiClaims = isAiClaimsPortalPath(pathname)
  const hasBothBrands = effectiveBrandAccess(user) === 'both'

  const canSwitchToAiClaims = hasBothBrands && !onAiClaims
  const canSwitchToWysehub = hasBothBrands && onAiClaims
  const canSwitchToWyseAi = isInternalEmployee(user?.rolenames)

  if (!canSwitchToAiClaims && !canSwitchToWysehub && !canSwitchToWyseAi) {
    return null
  }

  const goAiClaims = async () => {
    onAction?.()
    navigateToBrandPortal('aiclaims', AICLAIMS_WELCOME_PATH, async () => {
      await new UsersApi().usersSetActiveBrandMe({
        requestBody: { activeBrand: 'aiclaims' },
      })
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      router.push(AICLAIMS_WELCOME_PATH)
    })
  }

  const goWysehub = async () => {
    onAction?.()
    await new UsersApi().usersSetActiveBrandMe({
      requestBody: { activeBrand: 'wysehub' },
    })
    await queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    navigateToBrandPortal('wysehub', '/homehub', () => {
      router.push('/homehub')
    })
  }

  const goWyseAi = () => {
    onAction?.()
    navigateToBrandPortal('wyseai', '/app', () => {
      window.location.href = '/app'
    })
  }

  return (
    <>
      {canSwitchToAiClaims ? (
        <MenuItem onClick={() => void goAiClaims()}>
          <ListItemText primary="Switch to Ai Claims" />
        </MenuItem>
      ) : null}
      {canSwitchToWysehub ? (
        <MenuItem onClick={() => void goWysehub()}>
          <ListItemText primary="Switch to Wysehub" />
        </MenuItem>
      ) : null}
      {canSwitchToWyseAi ? (
        <MenuItem onClick={goWyseAi}>
          <ListItemText primary="Switch to WyseAi" />
        </MenuItem>
      ) : null}
    </>
  )
}
